import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageSquareQuote } from 'lucide-react';
import { phraseCount, type DepartmentPhrasesResponse } from '@his/shared';
import { get } from '@/lib/api';
import { tr } from '@/lib/i18n-core';
import { Button } from '@/components/ui/primitives';

export const EMR_FORMS_SETTINGS_PATH = '/admin/settings?tab=emr_forms';

export function departmentPhrasesPath(departmentId: string): string {
  return `/admin/emr-phrases/${encodeURIComponent(departmentId)}?returnTo=${encodeURIComponent(EMR_FORMS_SETTINGS_PATH)}`;
}

export function departmentPhrasesKey(departmentId: string) {
  return ['emr', 'department-phrases', departmentId] as const;
}

export function DepartmentPhrasesCard({ departmentId, editable }: { departmentId: string; editable: boolean }) {
  const navigate = useNavigate();
  const phrases = useQuery({
    queryKey: departmentPhrasesKey(departmentId),
    queryFn: () => get<DepartmentPhrasesResponse>(`/emr/department-phrases/${departmentId}`),
    enabled: Boolean(departmentId),
    staleTime: 60_000,
  });
  const count = phrases.data ? phraseCount(phrases.data.phrases) : null;

  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-sm" aria-labelledby="department-phrases-title">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-info/10 text-info">
          <MessageSquareQuote className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <h3 id="department-phrases-title" className="text-sm font-semibold">
            {tr('admin', 'dept_phrases_title')}
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {departmentId
              ? count === null
                ? tr('admin', 'dept_phrases_hint')
                : tr('admin', 'dept_phrases_count', { count })
              : tr('admin', 'dept_phrases_pick_department')}
          </p>
        </div>
        {departmentId && (
          <Button type="button" variant="outline" size="sm" onClick={() => navigate(departmentPhrasesPath(departmentId))}>
            {tr('admin', editable ? 'dept_phrases_edit' : 'dept_phrases_view')}
          </Button>
        )}
      </div>
    </section>
  );
}
